import { Consts, Envs, Utils } from '@common';
import { DateFNS, React, Styled, Styled as styled } from '@common/3rd';
import { DeskComponentRegistry } from '@component';
import { StyledComponents } from '@styles';
import { DeskPanel, DeskPanelBody, DeskPanelBodyCell } from '@desk-component';
import {
	CheckableWidgetState, StyledPropsTheme, DeskDateProps, DeskDateComponents,
} from '@my-types';
import { DeskText } from './desk-text';


class DeskDateFull<P extends DeskDateProps, S extends CheckableWidgetState & {
	showPicker: boolean;
	peddingValue: string;
}, C extends DeskDateComponents> extends DeskText<
P,
S,
C
> {
	labelRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);
		this.labelRef = React.createRef();
	}

	componentDidMount() {
		super.componentDidMount();
		this.forceUpdate();
	}
	protected initComponents(): C {
		const C = super.initComponents();
		return Object.assign(C, {
			Box: Styled(C.Box).attrs({
				'data-widget': 'desk-date'
			})``,
			Text: Styled(C.Text)`
			opacity: 0;
		`,
			DisplayLabel: Styled.span`
			display: block;
			position: absolute;
			height: ${props => props.theme.INPUT_HEIGHT}px;
			// width: 100%;
			border: 1px solid ${props => props.theme.BORDER_COLOR_LIGHT};
			border-radius: ${props => props.theme.BORDER_RADIUS}px;
			padding-left: ${props => props.theme.INPUT_HORIZONTAL_PADDING}px;
			pointer-events: none;
			max-width: ${props => props.theme.INPUT_MAX_WIDTH}px;
			${props => {
					const cText = document.getElementsByClassName(C.Text.styledComponentId);
					// console.log(cText);
					if (cText.length > 0) {
						const elem = cText[0] as Element;
						//@ts-ignore
						const elemWidth = elem.offsetWidth;
						// console.log(elemWidth);
						return `width:${elemWidth}px;`
					} else {
						return `width:100%;`
					}
				}}
			${props => this.renderDisplayTextCSS(props.theme)}
		`,
			DisplayLabelCaret: Styled(StyledComponents.FAR)`
			display: block;
			position: absolute;
			top: 0;
			right: 0;
			width: ${props => props.theme.INPUT_LINE_HEIGHT}px;
			height: ${props => props.theme.INPUT_HEIGHT}px;
			line-height: ${props => props.theme.INPUT_HEIGHT}px;
			color: ${props => props.theme.DATE_CARET_COLOR};
			font-size: ${props => props.theme.DATE_CARET_SIZE}px;
			cursor: pointer;
		` }) as C;
	}
	protected renderDisplayTextCSS(theme: StyledPropsTheme): string {
		if (this.isCheckedAnyway()) {
			// return `width: calc(100% - ${theme.INPUT_LINE_HEIGHT}px);`;
			return '';
		} else {
			return '';
		}
	}
	protected renderDisplayText(): JSX.Element {
		const C = this.getComponents();
		const DisplayLabelCaret = C.DisplayLabelCaret as any;
		return (
			<C.DisplayLabel ref={this.labelRef}>
				{this.getValueFromModel()}
				<DisplayLabelCaret icon="calendar" />
			</C.DisplayLabel>
		);
	}

	renderSuffixText() {
		const C = this.getComponents();
		return <C.SuffixText>
			{this.getSuffixText()}
		</C.SuffixText>;
	}

	render() {
		const C = this.getComponents();
		let input = <C.Text
			type="text"
			onClick={this.onDateFullClicked}
			readOnly={true}
			onFocus={this.onTextFocus}
			onBlur={this.onTextBlur}
			ref={this.textRef}
			defaultValue={this.asDOMAccepted(this.getValueFromModel())}
			id={this.getId()}
		/>
		{ this.renderDisplayText() }
		{ this.renderReminder() }
		{ this.renderSuffixText()}
		let selectPicker =
			<DateFullBoxPicker show={this.isDateFullBoxPickerShown()}
				value={this.asDOMAccepted(this.getValueFromModel())}
				pedValue={this.asDOMAccepted(this.getValueFromModel())}
				hide={this.hideDateFullBoxPicker}
				confirm={this.confirmDateFullBoxPickerValue}
			/>;
		//iphone 只读readonly,弹框，
		if (Envs.isIphone() && !this.isEditable()) {
			input = <C.Text
				onClick={this.onDateFullClicked}
				ref={this.textRef}
				readOnly={!this.isEditable()}
				disabled={true}
				defaultValue={this.asDOMAccepted(this.getValueFromModel())}
			/>;
		}
		return (
			<C.Box>
				{input}
				{this.renderDisplayText()}
				{selectPicker}
				{this.renderReminder()}
			</C.Box>
		);
	}

	private onDateFullClicked = () => {
		if (this.state.showPicker) {
			this.hideDateFullBoxPicker();
		} else {
			this.showDateFullBoxPicker();
		}
	}

	private isDateFullBoxPickerShown() {
		return this.state.showPicker ?? false;
	}

	private hideDateFullBoxPicker = () => {
		Envs.application()!.setScrollDisabled(false);
		this.setState({
			showPicker: false,
		});
	};

	private showDateFullBoxPicker = () => {
		if (!this.isEditable()) {
			return;
		}
		Envs.application()!.setScrollDisabled(true);
		this.setState({
			showPicker: true,
			peddingValue: this.initPeddingValue()
		});
	};

	protected initPeddingValue(): string {
		const cValue = this.asDOMAccepted(this.getValueFromModel());
		if (cValue && !Utils.isBlank(cValue)) {
			return cValue;
		}
		return '';
	}


	private confirmDateFullBoxPickerValue = async (value: string | null) => {
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		await this.setValueToModel(value).checkData();
	};

	protected paintColor(theme: StyledPropsTheme): string {
		const css = super.paintColor(theme);

		if (this.isCheckFailOnWhatever()) {
			return `${css} + span, + span > i {color: ${theme.COLOR_ERROR};}`;
		} else if (!this.isEditable()) {
			return `${css} + span > i {color: ${theme.COLOR_DISABLED};}`;
		} else {
			const focus = document.activeElement === this.textRef.current;
			return focus ? `${css} + span > i {color: ${theme.COLOR_PRIMARY};}` : css;
		}
	}
	protected asDOMAccepted(value?: string | null): string {
		// HTML 标准要求必须是横杠(-), 参考RFC3339
		return value ? this.formatValue(value, Consts.W3C_DATETIME_FORMAT) : '';
	}
	protected getFormat(): string {
		return (this.props.format || Consts.W3C_DATE_FORMAT) as string;
	}
	protected formatValue(value?: string | null, format?: string): string {
		let displayValue = '';
		if (value != null && !Utils.isEmpty(value)) {
			displayValue = DateFNS.format(value, format || this.getFormat());
		}
		return displayValue;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.textRef.current.value = this.asDOMAccepted(value);
			// this.checkData();
			this.forceUpdate();
		}
		return this;
	}
	protected getValueFromModel(propName?: string): any {
		const value = super.getValueFromModel(propName);
		if (!propName || propName === this.getPropName()) {
			return this.formatValue(value);
		} else {
			return value;
		}
	}
	protected setValueToModel(value: any, propName?: string): this {
		if (!propName || propName === this.getPropName()) {
			return super.setValueToModel(this.formatValue(value, Consts.STANDARD_DATETIME_FORMAT), propName);
		} else {
			return super.setValueToModel(value, propName);
		}
	}
	protected onTextBlur = (): void => {
		const value = this.textRef.current.value;
		this.setValueToModel(value).checkData();
		if (this.props.labellingParent) {
			if (this.props.labellingParent.onFocusLost) {
				this.props.labellingParent.onFocusLost();
			}
		}
	};
	protected onTextChanged = (): void => {
		const value = this.textRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}

		this.setValueToModel(value).checkData();
	};
}


const DateFullBox = styled.div`
	
	&[data-show=false] {
		pointer-events: none;
		display:none;
	}

	position: absolute;
    border-radius: 4px;
    background-color: #fff;
    z-index: 9999;
	box-shadow: 0 0 2px rgba(0, 0, 0, .3);

	.react-date-full-area-btn {
		padding: 5px 0;
		height: 40px;
		border-top: 1px solid #eee;
		text-align: center;
		.eg-btn {
			padding: 4px 20px;
			margin-right: 10px;
			&:last-child {
				margin-right: 0;
			}
		}

		.react-date-full-btn {
			padding: 4px 20px;
			border-radius: 6px;
			background-color: ${props => props.theme.COLOR_PRIMARY};
			display: inline-block;
			margin-right: 10px;
			margin-bottom: 0;
			text-align: center;
			vertical-align: middle;
			touch-action: manipulation;
			cursor: pointer;
			background-image: none;
			border: 1px solid transparent;
			white-space: nowrap;
			outline: 0;
			text-decoration: none;
			font-weight: normal;
			font-size: 14px;
			line-height: 1.42857143;
			-webkit-user-select: none;
			-moz-user-select: none;
			-ms-user-select: none;
			user-select: none;
			color: #fff;
			border-color: transparent;
			&:last-child {
				margin-right: 0;
			}
		}
		
		.react-date-full-btn-white {
			background-color: #fff;
			color: #333;
			border-color: #d5d5d5;
		}
	}
`;



const DateFullBoxPicker = (props: {
	readonly value?: string,
	pedValue: string,
	show: boolean,
	confirm: (value: string | null) => void,
	hide: () => void,
}) => {
	const {
		value, show, hide, confirm, pedValue,
	} = props;
	const dateTimeRef = React.useRef<HTMLInputElement>(null);

	const [, forceUpdate] = React.useReducer(x => x + 1, 0);

	const [, setPeddingValue] = React.useState<string>(pedValue);

	const fullDateTime = value ? DateFNS.format(new Date(value), 'YYYY-MM-DD HH:mm:ss') : '';
	const fullModel = { fullDateTime };


	React.useEffect(() => {
		(async () => {
			setPeddingValue(pedValue);
		})();
		return () => {
		};
	}, [pedValue]);


	const renderSelectOptBtn = (): JSX.Element | void => {
		return (
			<div className="react-date-full-area-btn">
				<button className="react-date-full-btn" onClick={handleConfirm}>确认</button>
				<button className="react-date-full-btn react-date-full-btn-white" onClick={handleCancel}>取消</button>
			</div>
		)
	}

	const handleConfirm = (): void => {
		const fdt = dateTimeRef.current!.value;
		let pValue = '';
		if (!Utils.isBlank(fdt)) {
			pValue = DateFNS.format(`${fdt}`, Consts.STANDARD_DATETIME_FORMAT);
		}
		confirm(pValue);
		hide();
	}

	const handleCancel = (): void => {
		hide();
	}


	return <DateFullBox data-show={show}>
		<DeskPanel model={fullModel}>
			<DeskPanelBody css={() => `padding:0;margin:10px;display:grid;grid-template-cloumns:1fr 1fr;`}>
				<DeskPanelBodyCell>
					<input style={{
						height: '32px',
						width: '260px'
					}} ref={dateTimeRef} type="datetime-local" id="fullDateTime" onChange={() => {
						forceUpdate();
					}} defaultValue={fullDateTime} />
				</DeskPanelBodyCell>
			</DeskPanelBody>
		</DeskPanel>
		{renderSelectOptBtn()}
	</DateFullBox>;
};

DeskComponentRegistry.register({
	dateFull: DeskDateFull
});

export { DeskDateFull };
